import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import './styles/home.css';
import './styles/site-error.css';
import './styles/game-card.css';
import './styles/theme-light.css';
import { AuthProvider, SettingsProvider, SlipProvider, LocaleProvider, useAuth, useLocale, useSettings } from './context';
import { useBalancePolling } from './hooks/useBalancePolling';
import { SiteContentProvider } from './context/SiteContentContext';
import { api } from './api';
import Header from './components/Header';
import NotificationBanner from './components/NotificationBanner';
import AppUpdatePopup from './components/AppUpdatePopup';
import HomePage from './components/HomePage';
import Footer from './components/Footer';
import MobileScrollTop from './components/mobile/MobileScrollTop';
import LoginModal from './components/auth/LoginModal';
import RegistrationModal from './components/auth/RegistrationModal';
import CasinoSearchModal from './components/casino/CasinoSearchModal';
import { resolvePromoClick, setPromoUrl } from './components/promos/promoUtils';
import PromoModalRouter from './components/home/PromoModalRouter';
import GuestWelcomePopup from './components/home/GuestWelcomePopup';
import SeoHead from './components/SeoHead';
import RouteFallback from './components/loading/RouteFallback';
import { BOOT_FAILSAFE_MS, dismissAppBoot, dismissAppBootWhenReady, forceDismissAppBoot, isAppBootOverlayActive, markAppBootStart } from './components/loading/dismissAppBoot';
import { buildTransitionKey, usePageTransition } from './hooks/usePageTransition';
import { pushRoute, readRoute, replaceRoute, resetPageScroll, isAuthRoute } from './navigation';
import {
  affiliateCodeFromPath,
  captureAffiliateClickFromUrl,
  clearAffiliateClickId,
  commitAffiliateTrackingUrl,
  stripAffiliateParamsFromUrl,
} from './utils/affiliateClick';
import { lockScroll, unlockScroll } from './scrollLock';
import { useIsMobile, useCompactCasinoGame } from './hooks/useIsMobile';
import { clearSiteStorage } from './utils/clearSiteStorage';
import { lazyRetry } from './utils/assetCache';
import { headerDisplayBalance } from './utils/walletBonusContext';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { useGuestWelcomePopup } from './hooks/useGuestWelcomePopup';
import { useSiteContent } from './context/SiteContentContext';
import { applyTheme } from './utils/theme';
import { historyCrumbForPage, historyIconKeyForPage, historyLabelForPage, pushBrowseHistory } from './utils/browseHistory';
import MobileBottomNav from './components/mobile/MobileBottomNav';
import SportNavChrome from './components/sport/SportNavChrome';
import MobileMenuDrawer from './components/mobile/MobileMenuDrawer';
import MobileAccountSheet from './components/mobile/MobileAccountSheet';
import SupportMessenger from './components/SupportMessenger';
import WowSmartHost from './wowsmart/WowSmartHost';
import NetworkStatusNotice from './components/NetworkStatusNotice';
import ApiReconnectNotice from './components/ApiReconnectNotice';
import EnCasingBoot from './components/EnCasingBoot';
import { setVisitorUserId, startVisitorBeacon, trackVisitorPath } from './utils/visitorBeacon';

const PromosPage = lazy(lazyRetry(() => import('./pages/PromosPage')));
const HelpPage = lazy(lazyRetry(() => import('./pages/HelpPage')));
const MobileHelpPage = lazy(lazyRetry(() => import('./pages/MobileHelpPage')));
const LoginPage = lazy(lazyRetry(() => import('./pages/LoginPage')));
const RegistrationPage = lazy(lazyRetry(() => import('./pages/RegistrationPage')));
const RestorePasswordPage = lazy(lazyRetry(() => import('./pages/RestorePasswordPage')));
const OauthCallbackPage = lazy(lazyRetry(() => import('./pages/OauthCallbackPage')));
const CasinoPage = lazy(lazyRetry(() => import('./pages/CasinoPage')));
const prefetchCasinoPageChunk = () => import('./pages/CasinoPage');
const CasinoGamePage = lazy(lazyRetry(() => import('./pages/CasinoGamePage')));
const AccountPage = lazy(lazyRetry(() => import('./pages/AccountPage')));
const MobileAccountPage = lazy(lazyRetry(() => import('./pages/MobileAccountPage')));
const VipPage = lazy(lazyRetry(() => import('./pages/VipPage')));
const VurgunTvPage = lazy(lazyRetry(() => import('./pages/VurgunTvPage')));
const CmsPage = lazy(lazyRetry(() => import('./pages/CmsPage')));
const BasindaBizPage = lazy(lazyRetry(() => import('./pages/BasindaBizPage')));
const HelpRouteNotFound = lazy(lazyRetry(() => import('./components/help/HelpRouteNotFound')));
const NexusSportsbook = lazy(lazyRetry(() => import('./components/sport/NexusSportsbook')));

function resolvePageForUser(page, hasSessionHint, needsProfileComplete = false) {
  if (page === 'oauthCallback') return 'oauthCallback';
  if (needsProfileComplete) return 'register';
  if (hasSessionHint && isAuthRoute(page) && !needsProfileComplete) return 'home';
  return page;
}

function MainApp() {
  const { user, loading: authLoading, token, refreshUser, logout } = useAuth();
  const _locale = useLocale();
  const tr = _locale.t;
  const { settings } = useSettings();
  const { guestWelcomePopup, loading: contentLoading, getPromoByPromoId } = useSiteContent();
  const contentLoaded = !contentLoading;
  const needsProfileComplete = Boolean(user && user.profileComplete === false);
  const hasSessionHint = Boolean(user) || Boolean(token);
  const initialRoute = readRoute();
  const [page, setPage] = useState(() => resolvePageForUser(initialRoute.page, hasSessionHint, needsProfileComplete));
  const [sportEvent, setSportEvent] = useState(() => initialRoute.sportEvent || null);
  const [accountSection, setAccountSection] = useState(() => initialRoute.accountSection);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCasinoSearch, setShowCasinoSearch] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  const [accountSheetSection, setAccountSheetSection] = useState(undefined);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  /** Keep Nexus iframe mounted after first sport visit so betslip survives menu navigation. */
  const [sportShellMounted, setSportShellMounted] = useState(() => page === 'sport');
  const accountMenuRef = useRef(null);
  const isMobile = useIsMobile();
  useEffect(() => {
    const ric = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 600));
    const id = ric(() => { prefetchCasinoPageChunk().catch(() => {}); }, { timeout: 2500 });
    return () => {
      if (window.cancelIdleCallback && typeof id === 'number') window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, []);
  const compactCasinoGame = useCompactCasinoGame();
  const [globalPromo, setGlobalPromo] = useState(null);
  const [casinoGameId, setCasinoGameId] = useState(() => initialRoute.casinoGameId);
  const [casinoGameDemo, setCasinoGameDemo] = useState(() => initialRoute.casinoGameDemo);
  const silenceRouteSyncRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      clearAffiliateClickId();
      stripAffiliateParamsFromUrl();
      return;
    }
    captureAffiliateClickFromUrl(window.location.search, { loggedIn: false });
  }, [authLoading, user]);

  useEffect(() => {
    if (authLoading) return;
    if (page !== 'affiliateGate') return;
    const code = affiliateCodeFromPath() || readRoute().affiliateCode;
    if (user) {
      clearAffiliateClickId();
      silenceRouteSyncRef.current = true;
      setPage('home');
      replaceRoute('home');
      queueMicrotask(() => { silenceRouteSyncRef.current = false; });
      return;
    }
    const commitUrl = commitAffiliateTrackingUrl(code);
    if (!commitUrl) {
      silenceRouteSyncRef.current = true;
      setPage('home');
      replaceRoute('home');
      queueMicrotask(() => { silenceRouteSyncRef.current = false; });
      return;
    }
    window.location.replace(commitUrl);
  }, [authLoading, user, page]);


  const bypassProfileGateRef = useRef(false);
  const bootDismissStartedRef = useRef(false);
  const [profileGateBypass, setProfileGateBypass] = useState(false);

  useBalancePolling(user ? (casinoGameId ? 2500 : 5000) : 0);

  useEffect(() => {
    const start = () => startVisitorBeacon();
    const ric = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1200));
    const handle = ric(start, { timeout: 2500 });
    return () => {
      if (window.cancelIdleCallback && typeof handle === 'number') window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  useEffect(() => {
    setVisitorUserId(user?.id || null);
  }, [user?.id]);

  useEffect(() => {
    trackVisitorPath();
  }, [page, accountSection, casinoGameId, filter]);

  useEffect(() => {
    if (!token) {
      setUnreadNotifications(0);
      return undefined;
    }
    let cancelled = false;
    api.accountSummary(token)
      .then((data) => {
        if (!cancelled && data) setUnreadNotifications(data.unread_notifications ?? 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const userLoggedIn = Boolean(user);

  const navigate = useCallback((nextPage, opts = {}) => {
    let pageToSet = nextPage;
    if (opts.force) {
      bypassProfileGateRef.current = true;
      setProfileGateBypass(true);
    }
    if (!opts.force && !bypassProfileGateRef.current && needsProfileComplete) {
      if (nextPage !== 'register' && nextPage !== 'oauthCallback') pageToSet = 'register';
    } else if (!opts.force && userLoggedIn && isAuthRoute(nextPage)) {
      pageToSet = 'home';
    }
    silenceRouteSyncRef.current = true;
    setPage(pageToSet);
    if (pageToSet === 'sport') setSportEvent(opts.sportEvent || null);
    else if (nextPage !== 'sport') setSportEvent(null);
    if (opts.accountSection) setAccountSection(opts.accountSection);
    if (pageToSet === 'sport') {
      const sf = opts.sportFilter;
      setFilter(sf === 'live' ? 'live' : (sf === 'line' || sf === 'prematch') ? 'line' : 'all');
    } else if (opts.sportFilter) {
      setFilter(opts.sportFilter);
    }
    if (nextPage === 'casino') {
      if ('casinoGameId' in opts) {
        setCasinoGameId(opts.casinoGameId || null);
        if ('casinoGameDemo' in opts) setCasinoGameDemo(Boolean(opts.casinoGameDemo));
      } else {
        setCasinoGameId(null);
        setCasinoGameDemo(false);
      }
    }
    if (opts.replace) replaceRoute(pageToSet, opts);
    else pushRoute(pageToSet, opts);
    queueMicrotask(() => {
      silenceRouteSyncRef.current = false;
    });
    if (pageToSet !== 'login' && pageToSet !== 'register') {
      try {
        const label = historyLabelForPage(pageToSet, opts, tr);
        const tabKey = opts.casinoLobbyId || opts.casinoTabId || opts.accountSection || opts.sportFilter || '';
        pushBrowseHistory({
          id: `${pageToSet}:${tabKey}`,
          label,
          crumb: historyCrumbForPage(pageToSet, opts, tr),
          pageId: pageToSet,
          opts: {
            ...(opts.casinoTabId ? { casinoTabId: opts.casinoTabId } : {}),
            ...(opts.casinoLobbyId != null ? { casinoLobbyId: opts.casinoLobbyId } : {}),
            ...(opts.accountSection ? { accountSection: opts.accountSection } : {}),
            ...(opts.sportFilter ? { sportFilter: opts.sportFilter } : {}),
          },
          icon: historyIconKeyForPage(pageToSet, opts),
        });
      } catch {}
    }
  }, [userLoggedIn, needsProfileComplete, tr]);

  const openCasinoSearch = useCallback(() => {
    setShowCasinoSearch(true);
  }, []);

  const closeCasinoSearch = useCallback(() => {
    setShowCasinoSearch(false);
  }, []);

  const openLoginModal = useCallback(() => {
    if (user) return;
    setShowRegisterModal(false);
    setShowLoginModal(true);
  }, [user]);

  const openLoginPage = useCallback(() => {
    if (user) return;
    setShowLoginModal(false);
    setShowRegisterModal(false);
    navigate('login');
  }, [navigate, user]);

  const activePage = useMemo(() => {
    if (page === 'oauthCallback') return 'oauthCallback';
    if (needsProfileComplete && !profileGateBypass) return 'register';
    if (user && isAuthRoute(page) && !needsProfileComplete) return 'home';
    return page;
  }, [user, page, needsProfileComplete, profileGateBypass]);

  const transitionKey = useMemo(
    () => buildTransitionKey(activePage, {
      casinoGameId,
      accountSection,
      cmsSlug: activePage === 'cms' ? readRoute().cmsSlug : null,
    }),
    [activePage, casinoGameId, accountSection, page],
  );
  const { displayKey, displayPage, displayCasinoGameId, pageBodyClass } = usePageTransition(transitionKey);

  useEffect(() => {
    if (activePage === 'sport' || displayPage === 'sport') {
      setSportShellMounted(true);
    }
  }, [activePage, displayPage]);

  useEffect(() => {
    markAppBootStart();
    const failSafe = window.setTimeout(() => {
      forceDismissAppBoot();
    }, BOOT_FAILSAFE_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isAppBootOverlayActive()) return;
      forceDismissAppBoot();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      window.clearTimeout(failSafe);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, []);

  useEffect(() => {
    if (bootDismissStartedRef.current) return undefined;
    if (authLoading) return undefined;
    // Dismiss boot as soon as auth settles — do not gate on CMS / hero image.
    // Hero continues loading under the real UI (skeleton → slides).
    bootDismissStartedRef.current = true;
    dismissAppBootWhenReady({ heroSrc: null });
    return undefined;
  }, [authLoading]);

  const handleOpenLogin = useCallback(() => {
    if (user) return;
    if (isMobile) {
      openLoginPage();
      return;
    }
    if (activePage === 'register' || activePage === 'login') {
      openLoginPage();
      return;
    }
    openLoginModal();
  }, [user, isMobile, activePage, openLoginPage, openLoginModal]);


  const globalPromoScrollLockedRef = useRef(false);

  const lockGlobalPromoScroll = useCallback(() => {
    if (globalPromoScrollLockedRef.current) return;
    lockScroll();
    globalPromoScrollLockedRef.current = true;
  }, []);

  const unlockGlobalPromoScroll = useCallback(() => {
    if (!globalPromoScrollLockedRef.current) return;
    unlockScroll();
    globalPromoScrollLockedRef.current = false;
  }, []);

  const openRegisterPage = useCallback(() => {
    if (user && !needsProfileComplete) return;
    setShowLoginModal(false);
    setGlobalPromo(null);
    setPromoUrl(null);
    unlockGlobalPromoScroll();
    if (isMobile || needsProfileComplete) {
      setShowRegisterModal(false);
      navigate('register', { force: Boolean(needsProfileComplete) });
      return;
    }
    setShowRegisterModal(true);
  }, [isMobile, navigate, user, needsProfileComplete, unlockGlobalPromoScroll]);

  useEffect(() => {
    const onOpenRegister = () => openRegisterPage();
    window.addEventListener('vb-open-register', onOpenRegister);
    return () => window.removeEventListener('vb-open-register', onOpenRegister);
  }, [openRegisterPage]);

  const openGlobalPromo = useCallback((promo) => {
    setGlobalPromo(promo);
    setPromoUrl(promo.promoId || promo.id);
    lockGlobalPromoScroll();
  }, [lockGlobalPromoScroll]);

  const closeGlobalPromo = useCallback(() => {
    setGlobalPromo(null);
    setPromoUrl(null);
    unlockGlobalPromoScroll();
  }, [unlockGlobalPromoScroll]);

  const openWelcomeLanding = useCallback(() => {
    const promo = getPromoByPromoId('TR_CRM_NC_welcome_freebet100_freespins300');
    if (promo) openGlobalPromo(promo);
  }, [openGlobalPromo, getPromoByPromoId]);

  const openPromo = useCallback((promoOrId) => {
    const promo = typeof promoOrId === 'string'
      ? getPromoByPromoId(promoOrId)
      : promoOrId;
    if (!promo) return;
    resolvePromoClick(promo, openGlobalPromo);
  }, [openGlobalPromo, getPromoByPromoId]);

  useEffect(() => {
    const onPopState = () => {
      const route = readRoute();
      let nextPage = route.page;
      if (needsProfileComplete && !bypassProfileGateRef.current) {
        nextPage = route.page === 'oauthCallback' ? route.page : 'register';
      } else if (user && isAuthRoute(route.page)) {
        nextPage = 'home';
      }
      if (nextPage !== route.page) replaceRoute(nextPage);
      setPage(nextPage);
      setSportEvent(route.sportEvent || null);
      setAccountSection(route.accountSection);
      setCasinoGameId(route.casinoGameId);
      setCasinoGameDemo(route.casinoGameDemo);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [user, needsProfileComplete]);

  useEffect(() => {
    const syncRoute = () => {
      if (silenceRouteSyncRef.current) return;
      const route = readRoute();
      let nextPage = route.page;
      if (needsProfileComplete && !bypassProfileGateRef.current) {
        nextPage = route.page === 'oauthCallback' ? route.page : 'register';
      } else if (user && isAuthRoute(route.page)) {
        nextPage = 'home';
      }
      setPage((prev) => (prev === nextPage ? prev : nextPage));
      setSportEvent((prev) => {
        const next = route.sportEvent || null;
        if (!prev && !next) return prev;
        if (prev && next
          && prev.gameId === next.gameId
          && prev.sportId === next.sportId
          && prev.leagueId === next.leagueId
          && prev.type === next.type
          && prev.oddId === next.oddId) return prev;
        return next;
      });
      setAccountSection((prev) => (prev === route.accountSection ? prev : route.accountSection));
      setCasinoGameId((prev) => (prev === route.casinoGameId ? prev : route.casinoGameId));
      setCasinoGameDemo((prev) => (prev === route.casinoGameDemo ? prev : route.casinoGameDemo));
    };
    window.addEventListener('vb-route-change', syncRoute);
    return () => window.removeEventListener('vb-route-change', syncRoute);
  }, [user, needsProfileComplete]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('clearSiteStorage') !== '1') return undefined;
    let cancelled = false;
    (async () => {
      await clearSiteStorage();
      if (cancelled) return;
      params.delete('clearSiteStorage');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState(window.history.state, '', next);
      window.location.reload();
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (needsProfileComplete && !bypassProfileGateRef.current) {
      if (page !== 'register' && page !== 'oauthCallback') {
        silenceRouteSyncRef.current = true;
        setPage('register');
        replaceRoute('register');
        queueMicrotask(() => { silenceRouteSyncRef.current = false; });
      }
      return;
    }
    if (!needsProfileComplete) {
      bypassProfileGateRef.current = false;
      setProfileGateBypass(false);
    }
    if (user && isAuthRoute(page) && page !== 'oauthCallback') {
      silenceRouteSyncRef.current = true;
      setPage('home');
      replaceRoute('home');
      queueMicrotask(() => { silenceRouteSyncRef.current = false; });
    }
  }, [authLoading, user, page, needsProfileComplete]);

  useEffect(() => {
    if (authLoading) return;
    if (needsProfileComplete) return;
    // Only bounce authenticated users — token alone must not block register/login (e.g. support → kayıt).
    if (user && token && isAuthRoute(page) && page !== 'oauthCallback') {
      silenceRouteSyncRef.current = true;
      setPage('home');
      replaceRoute('home');
      queueMicrotask(() => { silenceRouteSyncRef.current = false; });
    }
  }, [authLoading, user, token, page, needsProfileComplete]);

  useEffect(() => {
    if (needsProfileComplete) {
      setShowLoginModal(false);
      setShowRegisterModal(false);
      return;
    }
    if (user) {
      setShowLoginModal(false);
      setShowRegisterModal(false);
    }
  }, [user, needsProfileComplete]);

  useEffect(() => {
    resetPageScroll();
    const t1 = window.setTimeout(resetPageScroll, 0);
    const t2 = window.setTimeout(resetPageScroll, 120);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [displayKey]);

  useEffect(() => {
    const root = document.documentElement;
    if (activePage === 'casino') {
      root.dataset.casinoDark = '1';
      applyTheme('dark');
      return undefined;
    }
    delete root.dataset.casinoDark;
    applyTheme(settings.theme);
    return undefined;
  }, [activePage, settings.theme]);

  useEffect(() => {
    setLoading(true);
    api.events()
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activePage === 'home' || activePage === 'promos') return undefined;

    const syncPromoFromUrl = () => {
      const id = new URLSearchParams(window.location.search).get('promoId');
      const promo = getPromoByPromoId(id);
      if (promo) {
        setGlobalPromo((prev) => {
          const prevId = prev?.promoId || prev?.id || null;
          const nextId = promo?.promoId || promo?.id || null;
          if (prev && nextId && prevId === nextId) return prev;
          return promo;
        });
        lockGlobalPromoScroll();
        return;
      }
      setGlobalPromo(null);
      unlockGlobalPromoScroll();
    };

    syncPromoFromUrl();
    window.addEventListener('popstate', syncPromoFromUrl);
    return () => {
      window.removeEventListener('popstate', syncPromoFromUrl);
      unlockGlobalPromoScroll();
    };
  }, [activePage, getPromoByPromoId, lockGlobalPromoScroll, unlockGlobalPromoScroll]);

  const hideMobileChrome = activePage === 'login'
    || activePage === 'register'
    || activePage === 'affiliateGate'
    || activePage === 'restorePassword'
    || (activePage === 'casino' && casinoGameId)
    || (activePage === 'vip' && isMobile);

  const showMobileNav = isMobile && !hideMobileChrome;

  // Sport: nav overlays iframe; start hidden, SportNavChrome peek/swipe brings it back.
  useEffect(() => {
    if (!isMobile || activePage !== 'sport') {
      setMobileNavHidden(false);
      return;
    }
    setMobileNavHidden(true);
  }, [isMobile, activePage]);

  // Nexus scroll bridge may ask to show/hide parent tab bar — on sport only honor hide.
  useEffect(() => {
    if (!isMobile || activePage !== 'sport') return undefined;
    const onNav = (event) => {
      if (event?.detail?.hidden) setMobileNavHidden(true);
    };
    window.addEventListener('vb:mobile-nav', onNav);
    return () => window.removeEventListener('vb:mobile-nav', onNav);
  }, [isMobile, activePage]);

  const mobileNavCollapsedClass = showMobileNav && mobileNavHidden;
  const sportNavOverlay = showMobileNav && activePage === 'sport';

  const showFooter = displayPage !== 'login'
    && displayPage !== 'affiliateGate'
    && displayPage !== 'sport'
    && displayPage !== 'casino-game'
    && displayPage !== 'register'
    && displayPage !== 'account'
    && displayPage !== 'vip'
    && displayPage !== 'vurgunTv'
    && displayPage !== 'casino'
    && displayPage !== 'notFound';

  const guestPopupBlocked = showLoginModal
    || showRegisterModal
    || Boolean(globalPromo)
    || showCasinoSearch
    || mobileMenuOpen
    || isAuthRoute(activePage);

  const { visible: guestPopupVisible, dismiss: dismissGuestPopup } = useGuestWelcomePopup({
    enabled: contentLoaded,
    popup: guestWelcomePopup,
    user,
    authLoading,
    contentLoaded,
    blocked: guestPopupBlocked,
  });

  const handleAccountSheetChange = useCallback(({ open, section }) => {
    setAccountSheetOpen(Boolean(open));
    setAccountSheetSection(section);
  }, []);

  const closeAccountSheet = useCallback(() => {
    setAccountSheetOpen(false);
    setAccountSheetSection(undefined);
    accountMenuRef.current?.closeSheet?.();
  }, []);

  const headerHiddenForGame = Boolean(
    ((isMobile || (compactCasinoGame && casinoGameId)) && hideMobileChrome),
  );

  const openAccountSheet = useCallback((section) => {
    setMobileMenuOpen(false);
    if (!user) {
      handleOpenLogin();
      return;
    }
    // undefined / main / account → Hesabım hub; explicit sections pass through.
    const raw = section == null ? undefined : String(section);
    const nextSection = (!raw || raw === 'main' || raw === 'account') ? undefined : raw;
    // Mobile: always use App-owned sheet (single instance).
    if (isMobile || headerHiddenForGame || !accountMenuRef.current?.openSection) {
      setAccountSheetSection(nextSection);
      setAccountSheetOpen(true);
      return;
    }
    accountMenuRef.current.openSection(nextSection);
  }, [user, handleOpenLogin, headerHiddenForGame, isMobile]);

  // Sportsbook (nexustrike) posts { source:'sportsbook', action:'deposit' } → open cashier
  useEffect(() => {
    const onDeposit = () => {
      openAccountSheet('cashier');
    };
    window.addEventListener('vb:open-deposit', onDeposit);
    return () => window.removeEventListener('vb:open-deposit', onDeposit);
  }, [openAccountSheet]);

  const inGameSheetBalance = headerDisplayBalance(user, {
    page: activePage,
    route: { casinoGameId },
  }).amount;

  return (
    <div className={`app${isMobile ? ' app--mobile' : ''}${showMobileNav ? ' app--mobile-nav' : ''}${mobileNavCollapsedClass ? ' app--mobile-nav-collapsed' : ''}${sportNavOverlay ? ' app--sport-nav-overlay' : ''}${hideMobileChrome ? ' app--auth-screen' : ''}`}>
      {!headerHiddenForGame && (
      <>
      <NotificationBanner />
      <AppUpdatePopup />
      <Header
        page={activePage}
        onNavigate={navigate}
        onLogin={handleOpenLogin}
        onRegister={openRegisterPage}
        onCasinoSearch={openCasinoSearch}
        isMobile={isMobile}
        sportFilter={filter}
        unreadNotifications={unreadNotifications}
        onUnreadChange={setUnreadNotifications}
        accountMenuRef={accountMenuRef}
        onAccountSheetChange={handleAccountSheetChange}
      />
      </>
      )}
      {page === 'oauthCallback' && (
        <Suspense fallback={<RouteFallback />}>
          <OauthCallbackPage onNavigate={navigate} />
        </Suspense>
      )}
      <div className={`page-body${pageBodyClass ? ` ${pageBodyClass}` : ''}${displayPage === 'vip' ? ' page-body--vip' : ''}${displayPage === 'sport' ? ' page-body--sport' : ''}${displayPage === 'vurgunTv' ? ' page-body--vurgunTv' : ''}`}>
        {sportShellMounted && (
          <div
            className={`sport-keepalive${displayPage === 'sport' ? ' sport-keepalive--active' : ' sport-keepalive--hidden'}`}
            aria-hidden={displayPage !== 'sport'}
          >
            <div className={`sport-page-wrap${displayPage === 'sport' ? '' : ' sport-page-wrap--parked'}`}>
              <Suspense fallback={<RouteFallback page="sport" isMobile={isMobile} />}>
                <NexusSportsbook
                  onLogin={handleOpenLogin}
                  onNavigate={navigate}
                  sportEvent={sportEvent}
                  sportFilter={filter}
                />
              </Suspense>
            </div>
          </div>
        )}
        {displayPage !== 'sport' ? (
          <RouteErrorBoundary key={displayKey}>
          <Suspense fallback={<RouteFallback page={displayPage} isMobile={isMobile} casinoGameId={displayCasinoGameId} />}>
            <div className="page-enter" key={displayKey}>
              {displayPage === 'affiliateGate' ? (
                null
              ) : displayPage === 'home' ? (
                <HomePage
                  events={events}
                  loading={loading}
                  onNavigate={navigate}
                  onRegister={openRegisterPage}
                  onWelcomeLanding={openWelcomeLanding}
                  onPromoDetails={openPromo}
                  onCasinoSearch={openCasinoSearch}
                />
              ) : displayPage === 'promos' ? (
                <PromosPage onRegister={openRegisterPage} />
              ) : displayPage === 'help' ? (
                isMobile ? <MobileHelpPage onNavigate={navigate} /> : <HelpPage onNavigate={navigate} />
              ) : displayPage === 'login' ? (
                <LoginPage onNavigate={navigate} />
              ) : displayPage === 'restorePassword' ? (
                <RestorePasswordPage onNavigate={navigate} />
              ) : displayPage === 'register' ? (
                isMobile ? (
                  <RegistrationPage onNavigate={navigate} />
                ) : (
                  <>
                    <HomePage
                      events={events}
                      loading={loading}
                      onNavigate={navigate}
                      onRegister={openRegisterPage}
                      onWelcomeLanding={openWelcomeLanding}
                      onPromoDetails={openPromo}
                      onCasinoSearch={openCasinoSearch}
                    />
                    <RegistrationPage onNavigate={navigate} />
                  </>
                )
              ) : displayPage === 'casino' ? (
                displayCasinoGameId ? (
                  <CasinoGamePage
                    gameId={displayCasinoGameId}
                    isDemo={casinoGameDemo}
                    onNavigate={navigate}
                    onLogin={handleOpenLogin}
                    onRegister={openRegisterPage}
                    onCasinoSearch={openCasinoSearch}
                    onOpenAccount={openAccountSheet}
                  />
                ) : (
                  <CasinoPage onNavigate={navigate} onPromoDetails={openPromo} onLogin={handleOpenLogin} />
                )
              ) : displayPage === 'account' ? (
                isMobile ? (
                  <MobileAccountPage
                    onNavigate={navigate}
                    initialSection={accountSection}
                    onUnreadChange={setUnreadNotifications}
                  />
                ) : (
                  <AccountPage
                    onNavigate={navigate}
                    initialSection={accountSection}
                    onUnreadChange={setUnreadNotifications}
                  />
                )
              ) : displayPage === 'vip' ? (
                <VipPage onNavigate={navigate} onLogin={handleOpenLogin} />
              ) : displayPage === 'cms' ? (
                <CmsPage />
              ) : displayPage === 'vurgunTv' ? (
                <VurgunTvPage onLogin={handleOpenLogin} onNavigate={navigate} />
              ) : displayPage === 'basindaBiz' ? (
                <BasindaBizPage />
              ) : (
                <HelpRouteNotFound />
              )}
            </div>
          </Suspense>
          </RouteErrorBoundary>
        ) : !sportShellMounted ? (
          <Suspense fallback={<RouteFallback page="sport" isMobile={isMobile} />}>
            <RouteFallback page="sport" isMobile={isMobile} />
          </Suspense>
        ) : null}
        {showFooter && <Footer onNavigate={navigate} />}
      </div>
      {showLoginModal && !user && !isMobile && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onRegister={openRegisterPage}
          onForgot={() => { navigate('restorePassword'); pushRoute('restorePassword'); }}
        />
      )}
      {showRegisterModal && !user && !isMobile && (
        <RegistrationModal
          onClose={() => setShowRegisterModal(false)}
          onLogin={openLoginModal}
        />
      )}
      {globalPromo && (
        <PromoModalRouter
          promo={globalPromo}
          onClose={closeGlobalPromo}
          onRegister={openRegisterPage}
        />
      )}
      {guestPopupVisible && !user && (
        <GuestWelcomePopup
          popup={guestWelcomePopup}
          onClose={dismissGuestPopup}
          onRegister={openRegisterPage}
        />
      )}
      {showCasinoSearch && (
        <CasinoSearchModal
          onClose={closeCasinoSearch}
          onNavigate={navigate}
        />
      )}
      {showMobileNav && (
        <>
          <MobileScrollTop />
          <SportNavChrome
            active={sportNavOverlay}
            hidden={mobileNavHidden}
            onHiddenChange={setMobileNavHidden}
          />
          <MobileBottomNav
            activePage={activePage}
            accountSection={accountSection}
            menuOpen={mobileMenuOpen}
            accountSheetOpen={accountSheetOpen}
            accountSheetSection={accountSheetSection}
            sportFilter={filter}
            hidden={mobileNavHidden && activePage === 'sport'}
            onNavigate={navigate}
            onOpenMenu={() => setMobileMenuOpen(true)}
            onOpenAccount={openAccountSheet}
          />
        </>
      )}
      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onNavigate={navigate}
        onLogin={handleOpenLogin}
        onOpenAccount={openAccountSheet}
        onLogout={logout}
      />
      {(isMobile || headerHiddenForGame) && user ? (
        <MobileAccountSheet
          open={accountSheetOpen}
          initialSection={accountSheetSection}
          onClose={closeAccountSheet}
          balance={inGameSheetBalance}
          onNavigate={navigate}
          onLogout={logout}
          unreadNotifications={unreadNotifications}
          onUnreadChange={setUnreadNotifications}
        />
      ) : null}
      {activePage !== 'sport' && displayPage !== 'sport' ? (
        <SupportMessenger onRegister={openRegisterPage} />
      ) : null}
      {activePage !== 'sport' && displayPage !== 'sport' ? (
        <WowSmartHost onRegister={openRegisterPage} />
      ) : null}
      <NetworkStatusNotice />
      <ApiReconnectNotice />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
        <SiteContentProvider>
          <EnCasingBoot />
      <SeoHead />
          <SettingsProvider>
            <SlipProvider>
              <MainApp />
            </SlipProvider>
          </SettingsProvider>
        </SiteContentProvider>
      </LocaleProvider>
    </AuthProvider>
  );
}
